/**
 * 소셜 영상 유효 주소를 **또 물어야 하나** — 이어 묻기의 판정.
 *
 * `unsupported`·`unavailable` 은 다시 묻지 않는다. `pending`·`stale` 은 몇 번까지 다시 묻는다.
 * `ok` 라도 만료가 코앞이면 다시 묻는다(재생 도중 끊기지 않게).
 */

import type { VideoUrlResult } from 'src/types/search-all/video-url';

/**
 * 받는 중인 건을 다시 묻는 최대 횟수 — 넘으면 썸네일로 남긴다.
 * ⚠️ 광고는 백엔드가 광고 라이브러리 페이지를 채널당 3개씩 열어 받아 한 건에 수 초가 걸린다 — 한 화면 분량이 풀리는 데 1분 넘게 걸릴 수 있어 넉넉히 둔다.
 */
export const VIDEO_URL_MAX_ATTEMPTS = 40;

/**
 * 받는 중인 건을 다시 묻는 주기(ms) — **앞 요청을 보낸 때부터** 센다. 백엔드 권장 1.5~3초의 하한이다.
 * 🔑 상류가 기다려 준 시간(`waitMs`)은 이 주기에 들어간다 — 기다려 주는 백엔드면 답이 오자마자 곧 다시 묻고,
 *   `waitMs` 를 모르는 백엔드면 1.5초마다 묻는다(바로 답이 오므로 연달아 두드리지 않는다).
 * ⛔ 1.5초 밑으로 내리지 마라 — 백엔드 권장 하한이다.
 */
export const VIDEO_URL_RETRY_MS = 1500;

/**
 * 다시 묻기를 다 쓴 건을 접은 결과의 `reason` — 앱이 붙이는 값이다(백엔드는 주지 않는다).
 * `unsupported` 로 접혀 카드에 「미리보기 없음」으로 멈춘다(받는 중 표시가 영영 돌지 않게).
 */
export const VIDEO_URL_GAVE_UP_REASON = 'retryExhausted';

/** `ok` 주소의 만료가 이만큼(ms) 안으로 들어오면 다시 묻는다 */
export const VIDEO_URL_REFRESH_BEFORE_MS = 60 * 1000;

/**
 * @param result 받은 결과 — 아직 없으면 `undefined`
 * @param attempts 이 id 로 이미 물은 횟수
 * @param now 기준 시각 — 테스트가 고정한다
 */
export function shouldAskVideoUrl(result: VideoUrlResult | undefined, attempts: number, now: number = Date.now()): boolean {
  if (attempts >= VIDEO_URL_MAX_ATTEMPTS) return false;
  if (!result) return true;
  switch (result.status) {
    case 'ok':
      return result.expiresAt != null && result.expiresAt - now < VIDEO_URL_REFRESH_BEFORE_MS;
    case 'pending':
    case 'stale':
      return true;
    default:
      return false;
  }
}

/**
 * 아직 받는 중인데 다시 묻기를 다 썼나 — 그러면 더 기다리지 않고 `exhaustedVideoUrl` 로 접는다.
 * @param result 마지막 결과 — 요청이 통째로 실패해 없으면 `undefined`
 * @param attempts 이 id 로 이미 물은 횟수
 */
export function isVideoUrlExhausted(result: VideoUrlResult | undefined, attempts: number): boolean {
  if (attempts < VIDEO_URL_MAX_ATTEMPTS) return false;
  return !result || result.status === 'pending' || result.status === 'stale';
}

/**
 * 다시 묻기를 다 쓴 건의 멈춘 결과 — `unsupported` + `VIDEO_URL_GAVE_UP_REASON`.
 * @param id 게시물 id
 * @param last 마지막 결과 — 표지는 이어 쓴다
 */
export function exhaustedVideoUrl(id: string, last: VideoUrlResult | undefined): VideoUrlResult {
  return { id, status: 'unsupported', videoUrl: '', posterUrl: last?.posterUrl ?? '', expiresAt: null, reason: VIDEO_URL_GAVE_UP_REASON };
}

/**
 * 재생 주소의 오리진 — 미리 연결(`preconnect`)할 자리.
 * @returns `https://host[:port]` · http(s) 가 아니거나 읽을 수 없으면 빈 문자열
 */
export function videoOrigin(src: string): string {
  try {
    const url = new URL(src);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : '';
  } catch {
    return '';
  }
}

/**
 * 다음에 다시 물을 때까지 쉴 시간 — 앞 요청을 보낸 때부터 `VIDEO_URL_RETRY_MS` 가 차도록.
 * @param startedAt 앞 요청을 보낸 시각(ms)
 * @param now 답을 받은 시각(ms)
 * @returns 0 이상
 */
export function videoUrlRetryDelay(startedAt: number, now: number): number {
  return Math.max(0, VIDEO_URL_RETRY_MS - (now - startedAt));
}
